import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  downloadMedicineOrderInvoice,
  fetchMedicineOrderById,
  updateMedicineOrderClassic,
  updateMedicineOrderStatuses,
} from "api/medicineOrderImproved/medicineOrderImprovedApi";
import {
  enrichOrderPoLines,
  createOrderPoRefillRequest,
  fetchOrderPoMedicationStock,
  importOrderPoDocument,
  importOrderPoDocumentUrl,
  learnOrderPoLineMapping,
  searchOrderPoMedications,
  uploadOrderDocumentFiles,
} from "api/addOrderBeta/addOrderBetaApi";
import CommentSection from "components/CommentSection/CommentSection.jsx";
import {
  deleteOrderWiseComment,
  getallrider,
  getAllUsersWithOutPagination,
  getAllMedication,
  getOrderWiseComment,
  getStatusLogsByOrderId,
  searchMedicationForOrder,
  searchMedicationById,
  storeOrderComment,
  updateOrdersRider,
} from "../../../Listfunction";
import ScreenLoader from "views/Components/screenLoader";
import "./editOrderBeta.css";

const ORDER_STATUSES = {
  1: "Submitted",
  2: "Processing",
  3: "Dispatched",
  4: "Completed",
  5: "Cancelled",
  6: "Attempted",
  7: "Unreachable",
  8: "Payment Pending",
  9: "Quote Sent",
  10: "Contacted",
  11: "Packed",
  12: "Ready",
  13: "Draft",
  14: "Item Pending",
  16: "Partially Delivered",
  17: "Procurement Ready",
  18: "Hold",
  19: "Order On Move",
};

const SOURCE_LABELS = {
  1: "Flash",
  2: "oPharma",
  3: "MyPharma",
  4: "Daraz",
  5: "Pickme",
  6: "E-channeling",
  7: "Uber",
  8: "Heart 2 Heart",
  9: "Vida",
  10: "PetDoc",
  11: "LankaRemit",
  12: "ComBankFlash",
  13: "LiweCommunities",
  14: "Synergyit",
  15: "Delta",
  17: "DirectPay",
};

const DELIVERY_LABELS = {
  1: "Normal",
  2: "Express",
};

const STATUS_ACTIONS = {
  Draft: [2, 14, 5, 1],
  Submitted: [10, 2, 12, 14, 5],
  Contacted: [14, 2, 12, 5, 1],
  Processing: [11, 14, 12, 5, 1],
  "Item Pending": [14, 2, 11, 12, 17, 5, 1],
  Ready: [11, 2, 5, 1],
  Packed: [3, 8, 5, 1],
  Dispatched: [8, 5, 1],
  "Payment Pending": [1],
  "Procurement Ready": [5, 8, 1, 11, 12],
};

const getAuthToken = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return user.token || "";
};

const getOrderPayload = (payload) =>
  payload && payload.data ? payload.data : payload || {};

const cleanDisplayValue = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "none" || text.toLowerCase() === "nan") {
    return fallback;
  }
  return value;
};

const toSafeNumber = (value, fallback = 0) => {
  const cleaned = cleanDisplayValue(value, "");
  if (cleaned === "") return fallback;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : fallback;
};

const money = (value) =>
  toSafeNumber(value, 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const addonBenefitRows = (order) => {
  const benefits = order && Array.isArray(order.addon_benefits) ? order.addon_benefits : [];

  return benefits.filter((benefit) =>
    Number(benefit.delivery_discount) > 0 ||
    Number(benefit.discount_amount) > 0 ||
    Number(benefit.flash_points_used) > 0
  );
};

const addonBenefitPreviewRows = (order) => {
  if (addonBenefitRows(order).length > 0) return [];

  const preview = order && order.addon_benefit_preview ? order.addon_benefit_preview : {};
  const selected = Array.isArray(preview.selected) ? preview.selected : [];

  return selected.filter((benefit) =>
    Number(benefit.delivery_discount) > 0 ||
    Number(benefit.discount_amount) > 0 ||
    Number(benefit.flash_points_used) > 0
  );
};

const addonBenefitValue = (benefit) => {
  const deliveryDiscount = Number(benefit.delivery_discount || 0);
  const discountAmount = Number(benefit.discount_amount || 0);
  const flashPoints = Number(benefit.flash_points_used || 0);

  if (deliveryDiscount > 0) return `-${money(deliveryDiscount)} delivery`;
  if (discountAmount > 0) return `-${money(discountAmount)}`;
  if (flashPoints > 0) return `-${money(flashPoints)} points`;

  return "-";
};

const parseMoneyValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : "";
};

const getApiRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getOrderRiderId = (order) =>
  order
    ? order.rider_id ||
      (order.rider && order.rider.id) ||
      (order.riderdetails && order.riderdetails.id) ||
      ""
    : "";

const getOrderRiderLabel = (order) =>
  order
    ? order.rider_name ||
      (order.rider && order.rider.name) ||
      (order.riderdetails && order.riderdetails.name) ||
      ""
    : "";

const getMedicationTitle = (medication) =>
  medication
    ? medication.title || medication.name || medication.generic_name || "Selected medicine"
    : "";

const getMedicationPrice = (medication) =>
  medication
    ? medication.price ||
      medication.unit_price ||
      medication.selling_price ||
      (medication.pricing_summary && medication.pricing_summary.suggested_unit_price) ||
      ""
    : "";

const getMedicationItemCode = (medication) =>
  medication
    ? medication.item_code ||
      medication.code ||
      medication.medication_itemcode ||
      medication.itemCode ||
      ""
    : "";

const dateTime = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const minutesSince = (value) => {
  const date = new Date(value);

  if (!value || Number.isNaN(date.getTime())) {
    return 0;
  }

  return (Date.now() - date.getTime()) / 60000;
};

const getOrderWarning = (order) => {
  const orderStatus = Number(order.order_status);

  if (
    orderStatus === 2 &&
    order.process_start_time &&
    minutesSince(order.process_start_time) >= 60 * 24 * 3
  ) {
    return "Processing for 3+ days";
  }

  if (
    orderStatus === 1 &&
    Number(order.order_sub_type) === 1 &&
    order.created_at &&
    minutesSince(order.created_at) > 20
  ) {
    return "Submitted B2C order older than 20 minutes";
  }

  return "";
};

const getLeadName = (order) =>
  (order.lead && order.lead.name) ||
  order.subscriber_name ||
  order.customer_name ||
  "-";

const getMobile = (order) =>
  (order.lead && order.lead.mobile_no) ||
  (order.address && order.address.mobile) ||
  order.mobile ||
  "-";

const getEmail = (order) =>
  (order.lead && order.lead.email) ||
  (order.address && order.address.email) ||
  order.email ||
  "-";

const getCompany = (order) =>
  (order.lead &&
    order.lead.company &&
    (order.lead.company.company_name || order.lead.company.name)) ||
  (order.lead && order.lead.company_name) ||
  order.company_name ||
  "-";

const getAddressText = (address) => {
  if (!address) {
    return "-";
  }

  return [
    address.address_line1,
    address.address_line2,
    address.city_name || address.city,
    address.postal_code,
  ]
    .filter(Boolean)
    .join(", ");
};

const getItemMedicationId = (item) =>
  item.medication_id ||
  item.medicationId ||
  item.description ||
  (item.medication && item.medication.id) ||
  "";

const getHydratedMedicine = (item, itemDetails) => {
  const medicationId = getItemMedicationId(item);

  return (
    (medicationId && itemDetails[medicationId]) ||
    item.medicineData ||
    item.medication ||
    {}
  );
};

const getItemName = (item, itemDetails = {}) => {
  const medicine = getHydratedMedicine(item, itemDetails);

  return cleanDisplayValue(
    item.medication_title ||
    medicine.title ||
    medicine.name ||
      item.title,
    "",
  );
};

const getItemCode = (item, itemDetails = {}) => {
  const medicine = getHydratedMedicine(item, itemDetails);

  return cleanDisplayValue(
    item.medication_itemcode ||
    medicine.item_code ||
    medicine.code ||
    medicine.medication_itemcode ||
      item.item_code,
    "",
  );
};

const looksLikeItemCode = (value) => /^F-\d+/i.test(String(value || "").trim());

const getItemBarcode = (item, itemDetails = {}) => {
  const medicine = getHydratedMedicine(item, itemDetails);

  const barcode = item.barcode || item.medication_barcode || medicine.barcode || "";

  return cleanDisplayValue(
    looksLikeItemCode(barcode) ? "" : barcode,
    "",
  );
};

const getOrderItems = (order) =>
  order.orderItems ||
  order.order_items ||
  order.items ||
  order.medications ||
  order.order_medications ||
  [];

const getBatchRows = (item) => {
  const rawBatches =
    item.medicationBatches ||
    item.medicinBatchesdata ||
    item.batches ||
    item.batch_details ||
    [];

  if (Array.isArray(rawBatches)) {
    return rawBatches;
  }

  return rawBatches ? [rawBatches] : [];
};

const getBatchLabel = (batch) =>
  [
    batch.batch_no || batch.batchNo || batch.batch_number,
    batch.expiry_date || batch.expiryDate,
    batch.quantity || batch.qty ? `Qty ${batch.quantity || batch.qty}` : "",
  ]
    .filter(Boolean)
    .join(" | ");

const getStoreWiseMedication = (item, itemDetails = {}) => {
  const medicine = getHydratedMedicine(item, itemDetails);
  const rows =
    medicine.store_wise_medication ||
    item.store_wise_medication ||
    item.store_wise_medication_multiple ||
    [];

  return Array.isArray(rows) ? rows : [];
};

const getStockRowByPharmacy = (rows, pharmacyName) =>
  rows.find((row) => {
    const name =
      (row.pharmacies && row.pharmacies.name) ||
      (row.pharmacy && row.pharmacy.name) ||
      row.pharmacy_name ||
      "";

    return name.toLowerCase() === pharmacyName.toLowerCase();
  });

const getStockQuantity = (row) =>
  toSafeNumber(row && (row.quantity || row.available_quantity || row.qty), 0);

const getStockRowByType = (rows, targetType) =>
  rows.find((row) => {
    const name = String(
      (row.pharmacies && row.pharmacies.name) ||
        (row.pharmacy && row.pharmacy.name) ||
        row.pharmacy_name ||
        row.store_name ||
        "",
    ).toLowerCase();
    const type = String(
      (row.pharmacies && row.pharmacies.type) ||
        (row.pharmacy && row.pharmacy.type) ||
        row.store_type ||
        row.type ||
        "",
    ).toLowerCase();

    return (
      type === targetType ||
      name.includes(targetType) ||
      (targetType === "warehouse" && name.includes("store"))
    );
  });

const getItemPlacement = (item, itemDetails = {}) => {
  const medicine = getHydratedMedicine(item, itemDetails);

  if (item.location) {
    return item.location;
  }

  if (medicine.ycoord && medicine.xcoord) {
    return `${medicine.ycoord}${medicine.xcoord}`;
  }

  if (medicine.location) {
    return medicine.location;
  }

  return "";
};

const getItemInventory = (item, itemDetails = {}) => {
  const medicine = getHydratedMedicine(item, itemDetails);
  const rows = getStoreWiseMedication(item, itemDetails);
  const warehouse = getStockRowByPharmacy(rows, "Flash WareHouse");
  const pharmacy = getStockRowByPharmacy(rows, "Flash Health Pharmacy");
  const totalStock = rows.reduce((sum, row) => sum + getStockQuantity(row), 0);
  const pharmacyStock =
    item.pharmacyLevelQuantity !== undefined
      ? toSafeNumber(item.pharmacyLevelQuantity, 0)
      : getStockQuantity(pharmacy);
  const warehouseStock =
    item.storeLevelQuantity !== undefined
      ? toSafeNumber(item.storeLevelQuantity, 0)
      : getStockQuantity(warehouse);

  return {
    totalStock:
      item.totalAvailableQuantity !== undefined
        ? toSafeNumber(item.totalAvailableQuantity, 0)
        : totalStock || toSafeNumber(item.available_quantity || medicine.quantity, 0),
    pharmacyStock,
    warehouseStock,
    consumptionLevel: Math.ceil(
      toSafeNumber(
        medicine.daily_consumption ||
          item.consumtionLevel ||
          item.consumption_level,
        0,
      ),
    ),
    reorderLevel: toSafeNumber(medicine.reorder_level || item.reOrderLevel || item.reorder_level, 0),
    placement: getItemPlacement(item, itemDetails),
  };
};

const getBooleanValue = (...values) =>
  values.some((value) => value === true || value === 1 || value === "1");

const toApiBoolean = (value) => (getBooleanValue(value) ? true : false);

const getMappedComments = (rawComments) =>
  (rawComments || []).map((item, idx) => {
    const authorName =
      (item.user && item.user.name) ||
      (item.created_by && item.created_by.name) ||
      item.created_by_name ||
      item.user_name ||
      item.user ||
      "Unknown";
    const messageText =
      typeof item.comment === "string"
        ? item.comment
        : typeof item.text === "string"
        ? item.text
        : typeof item.message === "string"
        ? item.message
        : "";

    return {
      id: item.id || idx,
      author: authorName,
      text: messageText,
      timestamp: item.created_at || item.timestamp || new Date().toISOString(),
      avatar:
        item.avatar ||
        (item.user && item.user.profile_picture_url) ||
        "https://flash-prod.s3.ap-south-1.amazonaws.com/adminNewImage/avatar_icon.png",
    };
  });

const getItemKey = (item, index) =>
  String(item.order_item_id || item.localId || item.id || item.medication_id || index);

const createEmptyEditOrderItem = () => ({
  localId: `manual-new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: "",
  order_item_id: 0,
  medication_id: "",
  medication: {},
  quantity: 1,
  unit_price: 0,
  subtotal: 0,
  is_printable: false,
  partial_sent: false,
  claimable: false,
  is_recommended_product: false,
});

const getItemDraftValue = (draft, item, draftKey, ...fallbackKeys) => {
  if (draft && draft[draftKey] !== undefined) {
    return draft[draftKey];
  }

  for (let index = 0; index < fallbackKeys.length; index += 1) {
    const key = fallbackKeys[index];
    if (item && item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }

  return "";
};

const getOrderFlagValue = (draft, order, key, ...fallbackKeys) => {
  if (draft && draft[key] !== undefined) {
    return draft[key];
  }

  return getBooleanValue(
    ...fallbackKeys.map((fallbackKey) => order && order[fallbackKey]),
  );
};

const getFirstDefined = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const getPayloadValue = (...values) => {
  const value = getFirstDefined(...values);

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return getFirstDefined(value.value, value.id, value.key, value.label);
  }

  return value;
};

const getOrderStatusValue = (order, statusOverride = null) =>
  statusOverride !== null && statusOverride !== undefined
    ? statusOverride
    : getPayloadValue(order && order.order_status, order && order.status);

const normalizeBatchForPayload = (batch) => ({
  batch_no: batch.batch_no || batch.batchNo || batch.batch_number || "",
  expiry_date:
    batch.expiry_date && batch.expiry_date !== "N/A"
      ? batch.expiry_date
      : batch.expiryDate && batch.expiryDate !== "N/A"
      ? batch.expiryDate
      : "",
  quantity: getPayloadValue(batch.quantity, batch.qty, 0),
});

const getMedicationAvailableQuantity = (item) => {
  const storeWiseMedication = Array.isArray(item && item.store_wise_medication)
    ? item.store_wise_medication
    : [];

  if (storeWiseMedication.length) {
    return storeWiseMedication.reduce(
      (sum, row) => sum + Number(row.quantity || row.available_quantity || 0),
      0,
    );
  }

  return item ? item.total_quantity || item.quantity || "" : "";
};

const mapMedicineOption = (item) => {
  const storeWiseMedication = Array.isArray(item && item.store_wise_medication)
    ? item.store_wise_medication
    : [];
  const pharmacyRow =
    getStockRowByPharmacy(storeWiseMedication, "Flash Health Pharmacy") ||
    getStockRowByType(storeWiseMedication, "pharmacy");
  const warehouseRow =
    getStockRowByPharmacy(storeWiseMedication, "Flash WareHouse") ||
    getStockRowByType(storeWiseMedication, "warehouse");

  return {
    id: item.id,
    title: item.title || item.name || item.generic_name || "-",
    price: item.price || item.unit_price || item.selling_price || 0,
    itemCode: item.item_code || item.code || "",
    barcode: item.barcode || item.medication_barcode || "",
    availableQuantity: getMedicationAvailableQuantity(item),
    pharmacyLevelQuantity: getStockQuantity(pharmacyRow),
    storeLevelQuantity: getStockQuantity(warehouseRow),
    allocatedQuantity:
      (pharmacyRow &&
        (pharmacyRow.total_allocated || pharmacyRow.allocated_quantity)) ||
      (warehouseRow &&
        (warehouseRow.total_allocated || warehouseRow.allocated_quantity)) ||
      item.total_allocated ||
      item.allocated_quantity ||
      0,
    location:
      item.location ||
      item.rack_location ||
      item.bin_location ||
      (item.ycoord && item.xcoord ? `${item.ycoord}${item.xcoord}` : ""),
    reorderLevel: item.reorder_level || item.reOrderLevel || 0,
    consumptionLevel: item.daily_consumption || item.consumption_level || 0,
    raw: item,
  };
};

const soundex = (value) => {
  if (!value) return "";
  const source = String(value).toUpperCase().replace(/[^A-Z]/g, "");
  if (!source) return "";

  const first = source[0];
  const mapping = {
    B: "1",
    F: "1",
    P: "1",
    V: "1",
    C: "2",
    G: "2",
    J: "2",
    K: "2",
    Q: "2",
    S: "2",
    X: "2",
    Z: "2",
    D: "3",
    T: "3",
    L: "4",
    M: "5",
    N: "5",
    R: "6",
  };
  let previousCode = mapping[first] || "";
  let output = first;

  for (let index = 1; index < source.length; index += 1) {
    const code = mapping[source[index]] || "";
    if (code && code !== previousCode) output += code;
    previousCode = code;
  }

  return output.padEnd(4, "0").slice(0, 4);
};

const buildMedicineSearchIndex = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((medicine) => {
      const option = mapMedicineOption(medicine);
      const label = `${option.title} (${option.itemCode || ""})`;
      const searchable = [
        option.title,
        option.itemCode,
        option.barcode,
        medicine.placement,
        medicine.location,
      ]
        .filter(Boolean)
        .join(" ");

      return {
        ...option,
        label,
        labelLower: label.toLowerCase(),
        searchLower: searchable.toLowerCase(),
        soundex: soundex(searchable),
      };
    })
    .sort((a, b) => (a.labelLower > b.labelLower ? 1 : -1));

const filterLocalMedicineIndex = (index, query, limit = 50) => {
  const lower = query.trim().toLowerCase();
  const results = [];

  if (!lower) return results;

  index.forEach((medicine) => {
    if (results.length < limit && medicine.labelLower.startsWith(lower)) {
      results.push(medicine);
    }
  });

  index.forEach((medicine) => {
    if (
      results.length < limit &&
      !results.includes(medicine) &&
      medicine.searchLower.includes(lower)
    ) {
      results.push(medicine);
    }
  });

  if (!results.length && lower.length >= 3) {
    const querySoundex = soundex(lower);
    index.forEach((medicine) => {
      if (
        results.length < limit &&
        medicine.soundex &&
        medicine.soundex === querySoundex
      ) {
        results.push(medicine);
      }
    });
  }

  return results;
};

const parseJson = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const getActiveLeadPackage = (order) => {
  const packages =
    order.lead && Array.isArray(order.lead.lead_catalogue_package)
      ? order.lead.lead_catalogue_package
      : [];

  if (!packages.length) {
    return null;
  }

  return (
    packages.find(
      (item) => Number(item.package_id) === Number(order.origin_package_id),
    ) || packages.find(Boolean)
  );
};

const getPackageFeature = (leadPackage, featureTitle) => {
  const features = leadPackage
    ? leadPackage.lead_catalogue_package_feature || []
    : [];

  return features.find(
    (feature) =>
      feature.feature_title === featureTitle && Number(feature.is_active) === 1,
  );
};

const getPackageBenefits = (order) => {
  const leadPackage = getActiveLeadPackage(order);
  const cataloguePackage = leadPackage && leadPackage.catalogue_package;
  const packageName =
    (cataloguePackage && cataloguePackage.title) ||
    order.catalogue_package_name ||
    order.package_name ||
    "";
  const logo =
    (cataloguePackage && cataloguePackage.package_logo) ||
    (order.lead &&
      order.lead.b2b &&
      (order.lead.b2b.company_logo || order.lead.b2b.logo)) ||
    "";
  const benefits = [];
  const addBenefit = (label, value, tone = "") => {
    const normalizedValue =
      typeof value === "string" ? value.trim().toLowerCase() : value;

    if (
      value === undefined ||
      value === null ||
      value === "" ||
      normalizedValue === "null" ||
      normalizedValue === "null%" ||
      normalizedValue === "undefined" ||
      normalizedValue === "undefined%" ||
      normalizedValue === "nan" ||
      normalizedValue === "nan%" ||
      Number(value) === 0
    ) {
      return;
    }

    benefits.push({
      label,
      value,
      tone,
    });
  };

  const raya = getPackageFeature(leadPackage, "raya_flat_discount");
  const medicine = getPackageFeature(leadPackage, "medicine_discount");
  const delivery = getPackageFeature(leadPackage, "delivery");

  addBenefit("Raya", raya && `${raya.feature_value}%`);
  addBenefit("Medicine", medicine && `${medicine.feature_value}%`);

  parseJson(delivery && delivery.feature_value, []).slice(0, 3).forEach(
    (item, index) => {
      const deliveryLabel = ["Standard", "Express", "Island Wide"][index];
      addBenefit(`${deliveryLabel} free delivery`, item.free, "priority");
      addBenefit(`${deliveryLabel} delivery discount`, item.discount && `${item.discount}%`);
      addBenefit(
        `${deliveryLabel} base price`,
        item.base_price && money(item.base_price),
      );
    },
  );

  addBenefit(
    "Package delivery discount",
    order.package_delivery_discount && money(order.package_delivery_discount),
  );

  if (String(order.is_claimable) === "1") {
    benefits.push({
      label: "Automatic Claim Order",
      value: "Enabled",
      tone: "priority",
    });
  }

  if (order.is_insureme) {
    benefits.push({
      label: "InsureMe Order",
      value: "Enabled",
      tone: "priority",
    });
  }

  if (!packageName && !benefits.length) {
    return null;
  }

  return {
    packageName,
    logo,
    benefits,
  };
};

const getDocuments = (order) => {
  const documents = [];

  (order.prescriptions || []).forEach((item, index) => {
    const url = item.prescription_url || item.url || item;
    if (url) {
      documents.push({
        label: `Prescription ${index + 1}`,
        url,
      });
    }
  });

  (order.documents || []).forEach((item, index) => {
    const url = item.document_url || item.url || item;
    if (url) {
      documents.push({
        label: `Document ${index + 1}`,
        url,
      });
    }
  });

  (order.orderDocument || []).forEach((item, index) => {
    if (item.url) {
      documents.push({
        label: item.name || `Order document ${index + 1}`,
        url: item.url,
      });
    }
  });

  return documents;
};

const getPoDocuments = (order) => {
  const rows = order && Array.isArray(order.po_documents) ? order.po_documents : [];
  return rows
    .map((item, index) => ({
      label: `PO ${index + 1}`,
      url: item.po_document_url || item.document_url || item.url || item,
    }))
    .filter((item) => item.url);
};

const getOrderB2bId = (order) =>
  (order &&
    order.lead &&
    (order.lead.organization_id ||
      order.lead.b2b_id ||
      (order.lead.b2b && order.lead.b2b.id) ||
      (order.lead.company && order.lead.company.id))) ||
  order.b2b_id ||
  order.organization_id ||
  "";

const mapImportedPoItem = (item) => ({
  requestedName: item.requested_name || item.name || "",
  quantity: item.requested_quantity || item.quantity || 1,
  requestedUnit: item.requested_unit || item.unit || "",
  requestedPrice:
    parseMoneyValue(
      item.unit_price ||
        item.requested_unit_price ||
        item.quoted_unit_price ||
        item.po_unit_price ||
        item.price ||
        item.rate,
    ) || "",
  requestedTotal:
    parseMoneyValue(
      item.total_price ||
        item.requested_total ||
        item.line_total ||
        item.total ||
        item.amount,
    ) || "",
  selected: true,
  medication: null,
  availableQuantity: 0,
  refillRequested: false,
  refillQuantity: "",
  refillRemark: "",
});

const getShortageQuantity = (line) =>
  Math.max(0, toSafeNumber(line.quantity, 0) - toSafeNumber(line.availableQuantity, 0));

const getPoUnitPrice = (line) => {
  const unitPrice = parseMoneyValue(line.requestedPrice);
  if (unitPrice !== "") return unitPrice;

  const totalPrice = parseMoneyValue(line.requestedTotal);
  const quantity = toSafeNumber(line.quantity, 0);
  if (totalPrice !== "" && quantity > 0) {
    return totalPrice / quantity;
  }

  return "";
};

const getPoLineTotal = (line) => {
  const explicitTotal = parseMoneyValue(line.requestedTotal);
  if (explicitTotal !== "") return explicitTotal;
  const unitPrice = getPoUnitPrice(line);
  return unitPrice === "" ? 0 : toSafeNumber(line.quantity, 0) * unitPrice;
};

const getSystemLineTotal = (line) =>
  line.medication
    ? toSafeNumber(line.quantity, 0) * toSafeNumber(getMedicationPrice(line.medication), 0)
    : 0;

const InfoRow = ({ label, value }) => (
  <div className="eob-info-row">
    <span>{label}</span>
    <strong>{cleanDisplayValue(value, "")}</strong>
  </div>
);

const EditOrderBeta = ({ history, match }) => {
  const orderId = match.params.id;
  const [order, setOrder] = useState(null);
  const [itemDetails, setItemDetails] = useState({});
  const [itemDetailsLoading, setItemDetailsLoading] = useState(false);
  const [itemDrafts, setItemDrafts] = useState({});
  const [activeEditMedicineLineId, setActiveEditMedicineLineId] = useState("");
  const [editMedicineResults, setEditMedicineResults] = useState([]);
  const [editMedicineHighlightIndex, setEditMedicineHighlightIndex] = useState(0);
  const [medicineSearchIndex, setMedicineSearchIndex] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentError, setCommentError] = useState("");
  const [userListData, setUserListData] = useState([]);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderDraft, setOrderDraft] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [riderModalOpen, setRiderModalOpen] = useState(false);
  const [pendingRiderStatus, setPendingRiderStatus] = useState("");
  const [riderOptions, setRiderOptions] = useState([]);
  const [selectedRiderId, setSelectedRiderId] = useState("");
  const [riderError, setRiderError] = useState("");
  const [riderLoading, setRiderLoading] = useState(false);
  const [poFile, setPoFile] = useState(null);
  const [poNumberDraft, setPoNumberDraft] = useState("");
  const [poUploadedUrls, setPoUploadedUrls] = useState([]);
  const [poUploading, setPoUploading] = useState(false);
  const [poProcessing, setPoProcessing] = useState(false);
  const [poReviewOpen, setPoReviewOpen] = useState(false);
  const [poReviewLines, setPoReviewLines] = useState([]);
  const [poReviewSource, setPoReviewSource] = useState("");
  const [poMedicineSearchIndex, setPoMedicineSearchIndex] = useState(null);
  const [poMedicineSearchQuery, setPoMedicineSearchQuery] = useState("");
  const [poMedicineSearchResults, setPoMedicineSearchResults] = useState([]);
  const [poAppliedLines, setPoAppliedLines] = useState([]);
  const [poInsertedItems, setPoInsertedItems] = useState([]);
  const [poRefillCreated, setPoRefillCreated] = useState(false);
  const [poReviewApplying, setPoReviewApplying] = useState(false);
  const [deletedItemIds, setDeletedItemIds] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemFilter, setItemFilter] = useState("all");
  const medicineSearchCacheRef = useRef({});
  const medicineSearchRequestRef = useRef(0);
  const editMedicineInputRefs = useRef({});
  const editQuantityInputRefs = useRef({});
  const orderStoreId = order && order.store_id;

  const loadOrder = useCallback(() => {
    const token = getAuthToken();

    if (!token) {
      setError("Please log in again before loading this order.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    fetchMedicineOrderById(token, orderId)
      .then((payload) => {
        setOrder(getOrderPayload(payload));
      })
      .catch(() => {
        setError("Unable to load this order.");
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      setRiderOptions([]);
      return;
    }

    let isActive = true;
    setRiderLoading(true);

    getallrider("", 5, token)
      .then((payload) => {
        if (!isActive) return;

        setRiderOptions(
          getApiRows(payload)
            .filter((rider) => rider && rider.is_active === 1)
            .map((rider) => ({
              value: String(rider.id),
              label: rider.name || rider.username || `Rider ${rider.id}`,
            })),
        );
      })
      .catch(() => {
        if (isActive) {
          setRiderOptions([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setRiderLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!order) return;
    setPoNumberDraft(order.po_number || "");
    setPoUploadedUrls(getPoDocuments(order).map((document) => document.url));
  }, [order]);

  useEffect(() => {
    const token = getAuthToken();
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const store = orderStoreId || user.store_id || 1;

    if (!token) {
      setMedicineSearchIndex([]);
      return;
    }

    let isActive = true;

    getAllMedication(token, store)
      .then((response) => {
        if (!isActive) return;
        setMedicineSearchIndex(buildMedicineSearchIndex(getApiRows(response)));
      })
      .catch(() => {
        if (isActive) setMedicineSearchIndex([]);
      });

    return () => {
      isActive = false;
    };
  }, [orderStoreId]);

  const updateItemDraft = (itemKey, values) => {
    setItemDrafts((current) => ({
      ...current,
      [itemKey]: {
        ...(current[itemKey] || {}),
        ...values,
      },
    }));
  };

  const removeOrderItemRow = (item, index) => {
    const itemKey = getItemKey(item, index);
    const existingItemId =
      item.order_item_id !== undefined && item.order_item_id !== null
        ? item.order_item_id
        : item.id && String(item.id).indexOf("manual-new-") !== 0 && String(item.id).indexOf("po-new-") !== 0
        ? item.id
        : 0;

    if (existingItemId) {
      if (!window.confirm("Remove this item from the order when you save changes?")) {
        return;
      }
      setDeletedItemIds((current) =>
        current.includes(existingItemId) ? current : [...current, existingItemId],
      );
    } else {
      setPoInsertedItems((current) =>
        current.filter((candidate) => getItemKey(candidate, index) !== itemKey),
      );
    }

    setItemDrafts((current) => {
      const nextDrafts = { ...current };
      delete nextDrafts[itemKey];
      return nextDrafts;
    });
  };

  const updateOrderDraft = (key, value) => {
    setOrderDraft((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const loadCommentData = useCallback(() => {
    const token = getAuthToken();

    if (!token || !orderId) {
      return;
    }

    setCommentError("");

    Promise.allSettled([
      getOrderWiseComment(token, orderId, "medicine"),
      getStatusLogsByOrderId(token, orderId),
    ])
      .then(([commentResult, logResult]) => {
        const nextComments = [];

        if (commentResult.status === "fulfilled") {
          const rawComments =
            (commentResult.value &&
              commentResult.value.data &&
              commentResult.value.data.data) ||
            (commentResult.value && commentResult.value.data) ||
            [];
          nextComments.push(...getMappedComments(rawComments));
        }

        if (logResult.status === "fulfilled") {
          const rawLogs = (logResult.value && logResult.value.data) || [];
          nextComments.push(
            ...(rawLogs || []).map((log) => ({
              id: `status-${log.id}`,
              author: "System",
              text: `Order status changed to ${log.status} by ${
                (log.user && log.user.name) || log.channel || "system"
              }`,
              timestamp: log.created_at,
              avatar: "",
            })),
          );
        }

        setComments(nextComments);
      })
      .catch(() => setCommentError("Unable to load order comments."));
  }, [orderId]);

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      return;
    }

    getAllUsersWithOutPagination(token)
      .then((data) => {
        const users = (data || []).map((user) => ({
          value: user.id,
          label: user.name,
          email: user.email,
          role: user.role,
        }));

        setUserListData(users);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadCommentData();
  }, [loadCommentData]);

  useEffect(() => {
    const token = getAuthToken();

    if (!token || !order) {
      return;
    }

    const medicationIds = Array.from(
      new Set(
        getOrderItems(order)
          .map(getItemMedicationId)
          .filter(Boolean),
      ),
    ).filter((id) => !itemDetails[id]);

    if (!medicationIds.length) {
      return;
    }

    let isActive = true;
    setItemDetailsLoading(true);

    Promise.allSettled(
      medicationIds.map((medicationId) =>
        searchMedicationById(token, medicationId).then((response) => ({
          medicationId,
          data:
            response &&
            response.data &&
            response.data.data
              ? response.data.data
              : response && response.data,
        })),
      ),
    )
      .then((results) => {
        if (!isActive) {
          return;
        }

        const nextDetails = {};
        results.forEach((result) => {
          if (
            result.status === "fulfilled" &&
            result.value &&
            result.value.medicationId &&
            result.value.data
          ) {
            nextDetails[result.value.medicationId] = result.value.data;
          }
        });

        if (Object.keys(nextDetails).length) {
          setItemDetails((current) => ({ ...current, ...nextDetails }));
        }
      })
      .finally(() => {
        if (isActive) {
          setItemDetailsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [order, itemDetails]);

  useEffect(() => {
    const token = getAuthToken();

    if (!token || !activeEditMedicineLineId) {
      setEditMedicineResults([]);
      return;
    }

    const query =
      itemDrafts[activeEditMedicineLineId] &&
      itemDrafts[activeEditMedicineLineId].title
        ? itemDrafts[activeEditMedicineLineId].title.trim()
        : "";

    if (!query || query.length < 3) {
      setEditMedicineResults([]);
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const cacheKey = query.toLowerCase();
    medicineSearchRequestRef.current += 1;
    const requestId = medicineSearchRequestRef.current;
    const localResults = filterLocalMedicineIndex(medicineSearchIndex, query);

    if (localResults.length) {
      medicineSearchCacheRef.current[cacheKey] = localResults;
      setEditMedicineResults(localResults);
      setItemDetailsLoading(false);
      return;
    }

    if (medicineSearchCacheRef.current[cacheKey]) {
      setEditMedicineResults(medicineSearchCacheRef.current[cacheKey]);
      setItemDetailsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      setItemDetailsLoading(true);
      setEditMedicineResults([]);
      searchMedicationForOrder(
        query,
        (order && (order.country_id || (order.address && order.address.country_id))) || 1,
        1,
        token,
        (order && order.store_id) || user.store_id || 1,
      )
        .then((response) => {
          if (requestId !== medicineSearchRequestRef.current) return;
          const apiResults = getApiRows(response).map(mapMedicineOption);
          const cacheKeys = Object.keys(medicineSearchCacheRef.current);
          if (cacheKeys.length > 200) {
            delete medicineSearchCacheRef.current[cacheKeys[0]];
          }
          medicineSearchCacheRef.current[cacheKey] = apiResults;
          setEditMedicineResults(apiResults);
        })
        .catch(() => {
          if (requestId === medicineSearchRequestRef.current) {
            setEditMedicineResults([]);
          }
        })
        .finally(() => {
          if (requestId === medicineSearchRequestRef.current) {
            setItemDetailsLoading(false);
          }
        });
    }, 450);

    return () => clearTimeout(timer);
  }, [
    activeEditMedicineLineId,
    itemDrafts,
    medicineSearchIndex,
    order,
  ]);

  useEffect(() => {
    setEditMedicineHighlightIndex(0);
  }, [activeEditMedicineLineId, editMedicineResults]);

  const selectEditLineMedicine = (itemKey, medicine, focusQuantity = false) => {
    updateItemDraft(itemKey, {
      medicationId: medicine.id,
      title: medicine.title,
      unitPrice: medicine.price,
      available: true,
      itemCode: medicine.itemCode,
      barcode: looksLikeItemCode(medicine.barcode) ? "" : medicine.barcode,
      availableQuantity: medicine.availableQuantity,
      totalAvailableQuantity: medicine.availableQuantity,
      pharmacyLevelQuantity: medicine.pharmacyLevelQuantity,
      storeLevelQuantity: medicine.storeLevelQuantity,
      allocatedQuantity: medicine.allocatedQuantity,
      location: medicine.location,
      reOrderLevel: medicine.reorderLevel,
      consumtionLevel: medicine.consumptionLevel,
    });
    setItemDetails((current) => ({
      ...current,
      [medicine.id]: medicine.raw || medicine,
    }));
    setEditMedicineResults([]);
    setActiveEditMedicineLineId("");
    if (focusQuantity) {
      setTimeout(() => {
        if (editQuantityInputRefs.current[itemKey]) {
          editQuantityInputRefs.current[itemKey].focus();
          editQuantityInputRefs.current[itemKey].select();
        }
      }, 0);
    }
  };

  const handleEditMedicineKeyDown = (event, itemKey) => {
    if (activeEditMedicineLineId !== itemKey) {
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      return;
    }

    event.preventDefault();

    if (!editMedicineResults.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      setEditMedicineHighlightIndex((current) =>
        Math.min(current + 1, editMedicineResults.length - 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      setEditMedicineHighlightIndex((current) => Math.max(current - 1, 0));
      return;
    }

    const selectedMedicine =
      editMedicineResults[
        Math.min(editMedicineHighlightIndex, editMedicineResults.length - 1)
      ];
    if (selectedMedicine) {
      selectEditLineMedicine(itemKey, selectedMedicine, true);
    }
  };

  const focusEditMedicineInput = (itemKey) => {
    setTimeout(() => {
      if (editMedicineInputRefs.current[itemKey]) {
        editMedicineInputRefs.current[itemKey].focus();
      }
    }, 0);
  };

  const hydratePoLineStock = async (line) => {
    if (!line.medication || !line.medication.id) return line;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    let availableQuantity = Number(line.medication.stockQuantity || line.medication.total_quantity || 0);
    try {
      const stockRes = await fetchOrderPoMedicationStock(
        getAuthToken(),
        line.medication.id,
        user.store_id,
      );
      const rows = getApiRows(stockRes);
      if (rows.length) {
        availableQuantity = rows.reduce(
          (sum, row) => sum + Number(row.quantity || row.available_quantity || 0),
          0,
        );
      }
    } catch (error) {
      availableQuantity = Number(line.medication.stockQuantity || line.medication.total_quantity || 0);
    }

    const shortageQuantity = Math.max(
      0,
      Number(line.quantity || 0) - availableQuantity,
    );

    return {
      ...line,
      availableQuantity,
      medication: {
        ...line.medication,
        stockQuantity: availableQuantity,
      },
      refillRequested: shortageQuantity > 0 ? true : line.refillRequested,
      refillQuantity: shortageQuantity > 0 ? shortageQuantity : line.refillQuantity || "",
      refillRemark:
        shortageQuantity > 0 && !line.refillRemark
          ? `Auto refill from PO intake for ${line.requestedName}`
          : line.refillRemark,
    };
  };

  const processPoForReview = async ({ file = null, url = "", label = "" }) => {
    const token = getAuthToken();
    const b2bId = getOrderB2bId(order);

    if (!token || !order) return;
    if (!file && !url) {
      setError("Select or attach a PO document before processing.");
      return;
    }

    setPoProcessing(true);
    setError("");
    setSuccess("");
    try {
      const importResponse = file
        ? await importOrderPoDocument(token, file, b2bId, order.lead_id)
        : await importOrderPoDocumentUrl(token, url, b2bId, order.lead_id);
      const importData = importResponse && importResponse.data ? importResponse.data : {};
      const importedItems = Array.isArray(importData.items) ? importData.items : [];

      if (!importedItems.length) {
        setError("No PO item rows were found in this PO.");
        return;
      }

      const reviewLines = importedItems.map(mapImportedPoItem);
      const enrichedResponse = await enrichOrderPoLines(
        token,
        reviewLines.map((line) => ({
          requested_name: line.requestedName,
          requested_quantity: line.quantity,
          quoted_unit_price: line.requestedPrice || null,
        })),
        b2bId,
        order.lead_id,
      );
      const enrichedRows = getApiRows(enrichedResponse);
      const matchedLines = await Promise.all(
        reviewLines.map((line, index) =>
          hydratePoLineStock({
            ...line,
            medication:
              enrichedRows[index] && enrichedRows[index].medication
                ? enrichedRows[index].medication
                : null,
          }),
        ),
      );

      setPoReviewLines(matchedLines);
      setPoReviewSource(label || (file ? file.name : "Attached PO"));
      setPoRefillCreated(false);
      setPoReviewOpen(true);
    } catch (error) {
      setError("Could not process this PO document.");
    } finally {
      setPoProcessing(false);
    }
  };

  const uploadPoDocument = async () => {
    const token = getAuthToken();

    if (!token || !poFile) {
      setError("Choose a PO document before uploading.");
      return [];
    }

    setPoUploading(true);
    setError("");
    setSuccess("");

    try {
      const response = await uploadOrderDocumentFiles(token, [poFile], "po");
      const urls =
        (response && response.data && response.data.document_url) ||
        response.document_url ||
        [];
      const nextUrls = Array.isArray(urls) ? urls : [urls].filter(Boolean);

      if (!nextUrls.length) {
        setError("PO document upload did not return a document URL.");
        return [];
      }

      setPoUploadedUrls((current) => [...current, ...nextUrls]);
      setSuccess("PO document uploaded. Save PO details to attach it to this order.");
      return nextUrls;
    } catch (error) {
      setError("Could not upload the PO document.");
      return [];
    } finally {
      setPoUploading(false);
    }
  };

  useEffect(() => {
    const token = getAuthToken();
    if (poMedicineSearchIndex === null || !token) {
      setPoMedicineSearchResults([]);
      return;
    }

    const query = poMedicineSearchQuery.trim();
    if (query.length < 3) {
      setPoMedicineSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      searchOrderPoMedications(token, query, getOrderB2bId(order), order && order.lead_id)
        .then((response) => setPoMedicineSearchResults(getApiRows(response)))
        .catch(() => setPoMedicineSearchResults([]));
    }, 350);

    return () => clearTimeout(timer);
  }, [order, poMedicineSearchIndex, poMedicineSearchQuery]);

  const selectPoMedication = async (index, medication) => {
    const line = poReviewLines[index];
    const hydratedLine = await hydratePoLineStock({ ...line, medication });
    setPoReviewLines((current) =>
      current.map((candidate, lineIndex) =>
        lineIndex === index ? hydratedLine : candidate,
      ),
    );
    setPoMedicineSearchIndex(null);
    setPoMedicineSearchQuery("");
    setPoMedicineSearchResults([]);

    try {
      await learnOrderPoLineMapping(getAuthToken(), {
        b2b_id: getOrderB2bId(order),
        lead_id: order.lead_id,
        requested_name: line.requestedName,
        medication_id: medication.id,
        metadata: {
          source: "medicine_order_beta_edit_po_intake",
          po_unit_price: line.requestedPrice || null,
          order_id: order.id,
        },
      });
    } catch (error) {}
  };

  const updatePoReviewLine = (index, key, value) => {
    setPoReviewLines((current) =>
      current.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const updatedLine = { ...line, [key]: value };
        if (key === "quantity") {
          const shortageQuantity = getShortageQuantity(updatedLine);
          updatedLine.refillRequested = shortageQuantity > 0;
          updatedLine.refillQuantity = shortageQuantity > 0 ? shortageQuantity : "";
        }
        return updatedLine;
      }),
    );
  };

  const openPoMedicineSearch = (index) => {
    const line = poReviewLines[index] || {};
    setPoMedicineSearchIndex(index);
    setPoMedicineSearchQuery(
      line.medication ? getMedicationTitle(line.medication) : line.requestedName || "",
    );
  };

  const addPoReviewLine = () => {
    setPoReviewLines((current) => [
      ...current,
      {
        ...mapImportedPoItem({ requested_name: "" }),
        selected: true,
      },
    ]);
  };

  const recheckPoReviewLines = async () => {
    const token = getAuthToken();
    if (!token || !poReviewLines.length) return;

    setPoProcessing(true);
    setError("");
    setSuccess("");
    try {
      const enrichedResponse = await enrichOrderPoLines(
        token,
        poReviewLines.map((line) => ({
          requested_name: line.requestedName,
          requested_quantity: line.quantity,
          quoted_unit_price: line.requestedPrice || null,
        })),
        getOrderB2bId(order),
        order && order.lead_id,
      );
      const enrichedRows = getApiRows(enrichedResponse);
      const recheckedLines = await Promise.all(
        poReviewLines.map((line, index) =>
          hydratePoLineStock({
            ...line,
            medication:
              line.medication ||
              (enrichedRows[index] && enrichedRows[index].medication
                ? enrichedRows[index].medication
                : null),
          }),
        ),
      );
      setPoReviewLines(recheckedLines);
      setSuccess("PO matches rechecked.");
    } catch (error) {
      setError("Could not recheck PO matches.");
    } finally {
      setPoProcessing(false);
    }
  };

  const createRefillRequestForShortages = async (shortageLines) => {
    const refillLines = shortageLines.filter(
      (line) =>
        line.refillRequested &&
        line.medication &&
        line.medication.id &&
        toSafeNumber(line.refillQuantity || getShortageQuantity(line), 0) > 0,
    );

    if (!refillLines.length) return false;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const payload = {
      reference: poNumberDraft || order.po_number || "",
      request_date: new Date().toISOString(),
      status: 1,
      request_by: user.id,
      medications: refillLines.map((line) => ({
        id: line.medication.id,
        quantity: toSafeNumber(line.refillQuantity || getShortageQuantity(line), 0),
        remarks: line.refillRemark || "",
      })),
      store_id: user.store_id || order.store_id || 1,
    };

    const response = await createOrderPoRefillRequest(getAuthToken(), payload);
    return Boolean(
      response &&
        (response.success ||
          (response.data && response.data.success) ||
          response.status === true),
    );
  };

  const applyPoReviewLines = async () => {
    const selectedLines = poReviewLines.filter((line) => line.selected);
    if (!selectedLines.length) {
      setError("Select at least one PO line to apply.");
      return;
    }

    const mappedSelectedLines = selectedLines.filter(
      (line) => line.medication && line.medication.id,
    );
    const unmappedSelectedCount = selectedLines.length - mappedSelectedLines.length;
    if (!mappedSelectedLines.length) {
      setError("Select at least one matched Flash medicine before applying.");
      return;
    }

    const shortageLines = mappedSelectedLines.filter(
      (line) => getShortageQuantity(line) > 0,
    );
    const baseItems = getOrderItems(order);
    const nextDrafts = {};
    const nextInsertedItems = [];

    setPoReviewApplying(true);
    setError("");
    setSuccess("");
    try {
      const refillCreated =
        poRefillCreated || !shortageLines.length
          ? false
          : await createRefillRequestForShortages(shortageLines);

    mappedSelectedLines.forEach((line, index) => {
      const medication = line.medication || {};
      const targetItem = baseItems[index];
      const itemKey = targetItem
        ? getItemKey(targetItem, index)
        : `po-new-${index}-${medication.id}`;
      const draftValues = {
        medicationId: medication.id || "",
        title: getMedicationTitle(medication),
        quantity: line.quantity || 1,
        unitPrice: line.requestedPrice || getMedicationPrice(medication) || 0,
        available: Number(line.availableQuantity || 0) >= Number(line.quantity || 0),
        printable: false,
        claimable: false,
        upseller: false,
      };

      nextDrafts[itemKey] = draftValues;

      if (!targetItem) {
        nextInsertedItems.push({
          localId: itemKey,
          id: "",
          order_item_id: 0,
          medication_id: medication.id || "",
          medication,
          quantity: line.quantity || 1,
          unit_price: line.requestedPrice || getMedicationPrice(medication) || 0,
          subtotal:
            Number(line.quantity || 1) *
            Number(line.requestedPrice || getMedicationPrice(medication) || 0),
          is_printable: false,
          partial_sent: Number(line.availableQuantity || 0) < Number(line.quantity || 0),
          claimable: false,
          is_recommended_product: false,
          requested_name: line.requestedName,
        });
      }
    });

    setItemDrafts((current) => ({ ...current, ...nextDrafts }));
    setPoInsertedItems(nextInsertedItems);
    setPoAppliedLines(mappedSelectedLines);
    if (refillCreated) {
      setPoRefillCreated(true);
    }
    setPoReviewOpen(false);
    setSuccess(
      refillCreated
        ? `${mappedSelectedLines.length} matched PO line(s) applied and refill request created.${unmappedSelectedCount ? ` ${unmappedSelectedCount} unmatched line(s) were skipped.` : ""}`
        : `${mappedSelectedLines.length} matched PO line(s) applied to the item list.${unmappedSelectedCount ? ` ${unmappedSelectedCount} unmatched line(s) were skipped.` : ""} Save item changes to update the order.`,
    );
    } catch (error) {
      setError("Could not create the refill request. PO lines were not applied yet.");
    } finally {
      setPoReviewApplying(false);
    }
  };

  const addManualItemRows = (count = 1, focusFirst = false) => {
    const rows = Array.from({ length: count }, createEmptyEditOrderItem);
    setPoInsertedItems((current) => [...current, ...rows]);
    setItemDrafts((current) => {
      const nextDrafts = { ...current };
      rows.forEach((row) => {
        nextDrafts[getItemKey(row, 0)] = {
          medicationId: "",
          title: "",
          quantity: 1,
          unitPrice: 0,
          available: true,
          printable: false,
          claimable: false,
          upseller: false,
          packed: false,
        };
      });
      return nextDrafts;
    });
    if (focusFirst && rows[0]) {
      focusEditMedicineInput(getItemKey(rows[0], 0));
    }
    setSuccess(`${count} empty item row${count > 1 ? "s" : ""} added.`);
  };

  const handleEditQuantityKeyDown = (event, itemKey, visibleIndex) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    const nextRow = visibleItemRows[visibleIndex + 1];

    if (nextRow) {
      focusEditMedicineInput(nextRow.itemKey);
      return;
    }

    addManualItemRows(1, true);
  };

  const handleAddComment = (comment) => {
    const token = getAuthToken();

    if (!token) {
      setCommentError("Please log in again before posting a comment.");
      return;
    }

    const payload = {
      order_id: orderId,
      type: "medicine",
      comment: comment && comment.text ? comment.text : "",
      assigned_id: mentionedUsers.map((user) => user.id),
    };

    storeOrderComment(token, payload)
      .then(() => loadCommentData())
      .catch(() => setCommentError("Unable to post comment."));
  };

  const handleDeleteComment = (commentId) => {
    const token = getAuthToken();

    if (!token) {
      setCommentError("Please log in again before deleting a comment.");
      return;
    }

    deleteOrderWiseComment(token, commentId)
      .then(() => loadCommentData())
      .catch(() => setCommentError("Unable to delete comment."));
  };

  const statusLabel = ORDER_STATUSES[order && order.order_status] || "Unknown";
  const statusActions = (STATUS_ACTIONS[statusLabel] || []).filter(
    (status) => Number(status) !== Number(order && order.order_status),
  );
  const warning = order ? getOrderWarning(order) : "";
  const items = order
    ? [...getOrderItems(order), ...poInsertedItems].filter((item) => {
        const itemId =
          item.order_item_id !== undefined && item.order_item_id !== null
            ? item.order_item_id
            : item.id;
        return !deletedItemIds.map(String).includes(String(itemId));
      })
    : [];
  const itemRows = items.map((item, index) => {
    const itemKey = getItemKey(item, index);
    const itemDraft = itemDrafts[itemKey] || {};
    const itemWithDraft = {
      ...item,
      ...itemDraft,
      item_code: itemDraft.itemCode || item.item_code,
      medication_itemcode: itemDraft.itemCode || item.medication_itemcode,
      barcode: itemDraft.barcode !== undefined ? itemDraft.barcode : item.barcode,
      location: itemDraft.location || item.location,
    };
    const inventory = getItemInventory(itemWithDraft, itemDetails);
    const itemCode = getItemCode(itemWithDraft, itemDetails);
    const barcode = getItemBarcode(itemWithDraft, itemDetails);
    const itemTitle =
      itemDraft.title !== undefined
        ? itemDraft.title
        : getItemName(item, itemDetails);
    const unitPrice =
      itemDraft.unitPrice !== undefined
        ? itemDraft.unitPrice
        : item.unit_price || item.price;
    const quantity =
      itemDraft.quantity !== undefined ? itemDraft.quantity : item.quantity || 0;
    const isAvailable = getBooleanValue(
      itemDraft.available !== undefined
        ? itemDraft.available
        : item.partial_sent === false || item.isPartialOrderItem,
    );
    const isPrintable = getBooleanValue(
      itemDraft.printable !== undefined
        ? itemDraft.printable
        : item.is_printable || item.printable,
    );
    const isClaimable = getBooleanValue(
      itemDraft.claimable !== undefined
        ? itemDraft.claimable
        : item.claimable || item.isChecked,
    );
    const isUpseller = getBooleanValue(
      itemDraft.upseller !== undefined
        ? itemDraft.upseller
        : item.is_recommended_product || item.isUpSeller,
    );
    const isPacked = getBooleanValue(
      itemDraft.packed !== undefined
        ? itemDraft.packed
        : item.item_ordered || item.is_order_placed,
    );
    const subtotal =
      itemDraft.unitPrice !== undefined || itemDraft.quantity !== undefined
        ? Number(unitPrice || 0) * Number(quantity || 0)
        : item.subtotal ||
          (item.unit_price || item.price || 0) * (item.quantity || 0);
    const belowReorder =
      inventory.reorderLevel > 0 && inventory.totalStock <= inventory.reorderLevel;
    const searchText = [
      itemTitle,
      itemCode,
      barcode,
      inventory.placement,
      quantity,
      unitPrice,
    ]
      .join(" ")
      .toLowerCase();

    return {
      item,
      index,
      itemKey,
      itemDraft,
      inventory,
      itemCode,
      barcode,
      itemTitle,
      unitPrice,
      quantity,
      isAvailable,
      isPrintable,
      isClaimable,
      isUpseller,
      isPacked,
      subtotal,
      belowReorder,
      searchText,
    };
  });
  const normalizedItemSearch = itemSearch.trim().toLowerCase();
  const visibleItemRows = itemRows.filter((row) => {
    const matchesSearch =
      !normalizedItemSearch || row.searchText.includes(normalizedItemSearch);

    if (!matchesSearch) return false;
    if (itemFilter === "unpacked") return !row.isPacked;
    if (itemFilter === "shortage") return row.belowReorder || row.inventory.totalStock <= 0;
    if (itemFilter === "unmatched") return !row.itemTitle || row.itemTitle === "Selected medicine";
    return true;
  });
  const itemFilterCounts = {
    all: itemRows.length,
    unpacked: itemRows.filter((row) => !row.isPacked).length,
    shortage: itemRows.filter((row) => row.belowReorder || row.inventory.totalStock <= 0).length,
    unmatched: itemRows.filter((row) => !row.itemTitle || row.itemTitle === "Selected medicine").length,
  };
  const packageBenefits = useMemo(
    () => (order ? getPackageBenefits(order) : null),
    [order],
  );
  const documents = useMemo(() => (order ? getDocuments(order) : []), [order]);
  const poDocuments = useMemo(() => (order ? getPoDocuments(order) : []), [order]);
  const paidAmount = Number(order && order.paid_amount ? order.paid_amount : 0);
  const totalCost = Number(order && order.total_cost ? order.total_cost : 0);
  const pendingAmount =
    order && order.pending_amount !== undefined
      ? Number(order.pending_amount)
      : totalCost - paidAmount;
  const poSelectedCount = poReviewLines.filter((line) => line.selected).length;
  const poShortageCount = poReviewLines.filter(
    (line) => line.selected && line.medication && getShortageQuantity(line) > 0,
  ).length;
  const poRefillCount = poReviewLines.filter(
    (line) =>
      line.selected &&
      line.medication &&
      getShortageQuantity(line) > 0 &&
      line.refillRequested &&
      toSafeNumber(line.refillQuantity || getShortageQuantity(line), 0) > 0,
  ).length;
  const selectedPoReviewLines = poReviewLines.filter((line) => line.selected);
  const poReviewPoTotal = selectedPoReviewLines.reduce(
    (sum, line) => sum + getPoLineTotal(line),
    0,
  );
  const poReviewMedicationTotal = selectedPoReviewLines.reduce(
    (sum, line) => sum + getSystemLineTotal(line),
    0,
  );
  const poReviewTotalDifference = poReviewPoTotal - poReviewMedicationTotal;
  const poMissingPriceCount = selectedPoReviewLines.filter(
    (line) => getPoUnitPrice(line) === "",
  ).length;

  const buildClassicUpdatePayload = (
    extraPoUrls = [],
    statusOverride = null,
    overrides = {},
  ) => {
    const address = (order && order.address) || {};
    const lead = (order && order.lead) || {};
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const status = getOrderStatusValue(order, statusOverride);
    const mappedItems = items.map((item, index) => {
      const itemKey = getItemKey(item, index);
      const draft = itemDrafts[itemKey] || {};
      const medicationId =
        draft.medicationId ||
        item.medication_id ||
        (item.medication && item.medication.id) ||
        item.id;
      const quantity = Number(
        getItemDraftValue(draft, item, "quantity", "quantity") || 0,
      );
      const unitPrice = Number(
        getItemDraftValue(draft, item, "unitPrice", "unit_price", "price") || 0,
      );
      const marginDiscountPercentage = Number(
        item.margin_discount_percentage || item.marginDiscountPercentage || 0,
      );
      const marginDiscountPrice = Number(
        item.margin_discount_price || item.marginDiscountPrice || 0,
      );
      const available = getItemDraftValue(
        draft,
        item,
        "available",
        "isPartialOrderItem",
      );

      return {
        id: medicationId,
        quantity,
        item_type: item.item_type || 1,
        margin_discount_sub_type: item.margin_discount_sub_type || null,
        margin_discount_price: marginDiscountPrice,
        margin_discount_percentage: marginDiscountPercentage,
        item_id:
          item.order_item_id !== undefined && item.order_item_id !== null
            ? item.order_item_id
            : item.id && String(item.id).indexOf("po-new-") !== 0
            ? item.id
            : 0,
        claimable: toApiBoolean(
          getItemDraftValue(draft, item, "claimable", "claimable", "isChecked"),
        ),
        is_recommended_product: toApiBoolean(
          getItemDraftValue(
            draft,
            item,
            "upseller",
            "is_recommended_product",
            "isUpSeller",
          ),
        ),
        partial_sent: !toApiBoolean(
          available !== "" ? available : item.partial_sent === false,
        ),
        item_description:
          getItemDraftValue(draft, item, "note", "description", "item_description") ||
          "",
        unit_price: unitPrice,
        is_printable: toApiBoolean(
          getItemDraftValue(draft, item, "printable", "is_printable", "printable"),
        ),
        item_ordered: toApiBoolean(
          getItemDraftValue(draft, item, "packed", "item_ordered", "is_order_placed"),
        ),
        batches: getBatchRows(item).map(normalizeBatchForPayload),
        refill_requested: toApiBoolean(item.refill_requested || item.refillRequested),
      };
    }).filter((item) => item.item_id !== 0 || item.id);
    const mappedBatches = mappedItems.flatMap((item) => item.batches || []);
    const orderSource = getPayloadValue(
      order.source,
      order.source_id,
      order.order_source,
      1,
    );
    const prescriptionUrls = getFirstDefined(
      order.prescription_file_url,
      order.prescription_file,
      order.prescription_url,
      [],
    );
    const storeId = getPayloadValue(order.store_id, user.store_id, 1);
    const isStoreOrder = Number(storeId) !== 1;
    const addressId = getFirstDefined(
      order.address_id,
      address.id,
      isStoreOrder ? 0 : "",
      0,
    );
    const addressLine1 = getFirstDefined(
      address.address_line1,
      address.address,
      order.address_line1,
    );
    const addressLine2 = getFirstDefined(
      address.address_line2,
      order.address_line2,
      isStoreOrder ? "ambalangoda" : "",
    );
    const cityId = getPayloadValue(
      address.city_id,
      address.city,
      order.city,
      isStoreOrder ? 4 : "",
    );
    const countryId = getPayloadValue(
      address.country_id,
      address.country,
      order.country_id,
      isStoreOrder ? 1 : "",
      1,
    );
    const partialItemsIncluded = mappedItems.some((item) => !item.partial_sent);
    const orderSubType = getPayloadValue(order.order_sub_type, 1);
    const isPoOrder = Number(orderSubType) === 2 || Number(orderSubType) === 4;
    const billingAddressId = isPoOrder
      ? getFirstDefined(order.billing_address_id, addressId)
      : addressId;

    return {
      lead_id: getFirstDefined(order.lead_id, lead.id),
      source: orderSource,
      opharma_reference: getFirstDefined(
        order.flash_reference,
        order.flashreference,
        order.opharma_reference,
      ),
      order_date: getFirstDefined(order.order_date, order.created_at),
      prescription_file: prescriptionUrls,
      prescription_file_url: prescriptionUrls,
      prescription_text: order.prescription_text || "",
      delivery_type: getPayloadValue(order.delivery_type, 1),
      delivery_charge: Number(order.delivery_charge || 0),
      comments: order.comments || "",
      address_id: addressId,
      address_line1: addressLine2,
      address_line2: addressLine1,
      city: cityId,
      country_id: countryId,
      postal_code: address.postal_code || "",
      channel: order.channel || "admin",
      not_get_medicine: order.not_get_medicine || "",
      medications: mappedItems,
      request_quote_notification: order.request_quote_notification || null,
      shipping_name: getFirstDefined(address.name, lead.name, order.shipping_name),
      shipping_email: getFirstDefined(address.email, lead.email, order.shipping_email),
      shipping_mobile: getFirstDefined(address.mobile, lead.mobile, order.shipping_mobile),
      shipping_phone: getFirstDefined(address.phone, lead.phone, order.shipping_phone),
      order_status: status,
      order_status_label: ORDER_STATUSES[Number(status)] || "",
      margin_type: order.margin_type || 0,
      margin: order.margin || 0,
      organization_discount: order.organization_discount || 0,
      tags: order.tags || order.health_conditions || "",
      paymentoption: getPayloadValue(
        order.paymentoption,
        order.payment_option,
        order.payment_type,
        "Cash",
      ),
      payment_due_date: order.payment_due_date || "",
      order_note: order.order_note || order.note || "",
      mypharma_reference: order.mypharma_reference || "",
      rider_id: getPayloadValue(order.rider_id, order.rider),
      is_courier_partner: getPayloadValue(order.is_courier_partner, 0),
      tracking_id: order.tracking_id || order.currierTrackingId || "",
      tracking_note: order.tracking_note || order.currierTrackingNote || "",
      tracking_url: order.tracking_url || order.currierTrackingUrl || "",
      sales_user_id: order.sales_user_id || "",
      deleted_item_id: deletedItemIds,
      delivery_from_date: order.delivery_from_date || "",
      delivery_to_date: order.delivery_to_date || "",
      order_sub_type: orderSubType,
      po_number: isPoOrder ? poNumberDraft || "" : "",
      po_file: isPoOrder ? [...poUploadedUrls, ...extraPoUrls].filter(Boolean) : [],
      grn_file: isPoOrder ? getFirstDefined(order.grn_file, order.grn_document_url, []) : [],
      is_refillable: getOrderFlagValue(
        orderDraft,
        order,
        "refillable",
        "refillable_order",
        "is_refillable",
      ),
      partial_sent: partialItemsIncluded,
      is_abusable: getOrderFlagValue(
        orderDraft,
        order,
        "abusable",
        "abusable_drug",
        "is_abusable_drug",
        "is_abusable",
      ),
      item_description: order.item_description || "",
      is_claimable: getOrderFlagValue(
        orderDraft,
        order,
        "claimOrder",
        "claim_order",
        "is_claim_order",
        "is_claimable",
      )
        ? "1"
        : "0",
      latitude: address.latitude || "",
      longitude: address.longitude || "",
      address_type: address.address_type || "",
      customaddress: address.customaddress || "",
      is_insureme: order.is_insureme || false,
      store_id: storeId,
      billing_address_id: billingAddressId,
      pr_id: order.pr_id || null,
      batches: mappedBatches,
      po_values: order.po_values || "",
      schedule_order_datetime: order.schedule_order_datetime || "",
      ...overrides,
    };
  };

  const saveOrderItemChanges = () => {
    const token = getAuthToken();

    if (!token || !order) return;

    setSaving(true);
    setError("");
    setSuccess("");

    updateMedicineOrderClassic(token, order.id, buildClassicUpdatePayload())
      .then(() => {
        setSuccess("Order item changes saved.");
        setItemDrafts({});
        setOrderDraft({});
        setPoInsertedItems([]);
        setDeletedItemIds([]);
        loadOrder();
      })
      .catch(() => {
        setError("Could not save the item changes. Please check required order details.");
      })
      .finally(() => setSaving(false));
  };

  const savePoDetails = async (extraPoUrls = []) => {
    const token = getAuthToken();

    if (!token || !order) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateMedicineOrderClassic(token, order.id, buildClassicUpdatePayload(extraPoUrls));
      setSuccess("PO details saved.");
      loadOrder();
    } catch (error) {
      setError("Could not save PO details.");
    } finally {
      setSaving(false);
    }
  };

  const saveFullUpdate = async () => {
    const token = getAuthToken();

    if (!token || !order) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateMedicineOrderClassic(token, order.id, buildClassicUpdatePayload());
      setSuccess("Full update saved.");
      setItemDrafts({});
      setOrderDraft({});
      setPoInsertedItems([]);
      setDeletedItemIds([]);
      loadOrder();
    } catch (error) {
      setError("Could not save the full update. Please check the changed items and order details.");
    } finally {
      setSaving(false);
    }
  };

  const buildStatusUpdateOrder = (status) => {
    const nextOrder = {
      id: order.id,
      status,
    };

    if (order.delivery_from_date) {
      nextOrder.delivery_from_date = order.delivery_from_date;
    }

    if (order.delivery_to_date) {
      nextOrder.delivery_to_date = order.delivery_to_date;
    }

    if (order.vehicle_type) {
      nextOrder.vehicle_type = order.vehicle_type;
    }

    if (order.rider_id) {
      nextOrder.rider_id = order.rider_id;
    }

    if (order.is_courier_partner !== undefined && order.is_courier_partner !== null) {
      nextOrder.is_courier_partner = order.is_courier_partner;
    }

    return nextOrder;
  };

  const updateStatus = async (status, reason = "", message = "") => {
    const token = getAuthToken();
    const nextStatus = ORDER_STATUSES[status] || "Unknown";

    if (!token || !order) {
      return;
    }

    if (
      status !== 5 &&
      !window.confirm(`Change order #${order.id} to ${nextStatus}?`)
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateMedicineOrderStatuses(
        token,
        [buildStatusUpdateOrder(status)],
        reason,
        message,
      );
      await updateMedicineOrderClassic(
        token,
        order.id,
        buildClassicUpdatePayload([], status),
      );
      setSuccess(`Order moved to ${nextStatus}.`);
      setCancelOpen(false);
      setCancelReason("");
      setCancelMessage("");
      loadOrder();
    } catch (error) {
      setError("Unable to update order status.");
    } finally {
      setSaving(false);
    }
  };

  const openRiderStatusModal = (status) => {
    const currentRiderId = getOrderRiderId(order);

    setPendingRiderStatus(status);
    setSelectedRiderId(currentRiderId ? String(currentRiderId) : "");
    setRiderError("");
    setRiderModalOpen(true);
  };

  const closeRiderStatusModal = () => {
    setRiderModalOpen(false);
    setPendingRiderStatus("");
    setSelectedRiderId("");
    setRiderError("");
  };

  const updateStatusWithRider = async () => {
    const token = getAuthToken();
    const nextStatus = ORDER_STATUSES[pendingRiderStatus] || "Unknown";

    if (!token || !order) {
      return;
    }

    if (!selectedRiderId) {
      setRiderError("Rider is required.");
      return;
    }

    setSaving(true);
    setRiderError("");
    setError("");
    setSuccess("");

    try {
      await updateOrdersRider(
        [order.id],
        selectedRiderId,
        pendingRiderStatus,
        0,
        "",
        "",
        "",
        order.store_id || JSON.parse(localStorage.getItem("user") || "{}").store_id || 1,
        token,
        nextStatus,
      );
      await updateMedicineOrderClassic(
        token,
        order.id,
        buildClassicUpdatePayload([], pendingRiderStatus, {
          rider_id: selectedRiderId,
          is_courier_partner: 0,
        }),
      );
      setSuccess(`Order moved to ${nextStatus}.`);
      closeRiderStatusModal();
      loadOrder();
    } catch (error) {
      setRiderError("Unable to update rider and status.");
    } finally {
      setSaving(false);
    }
  };

  const printInvoice = () => {
    const token = getAuthToken();

    if (!token) {
      setError("Please log in again before printing the invoice.");
      return;
    }

    setError("");

    downloadMedicineOrderInvoice(token, orderId)
      .then((response) => {
        const pdfUrl =
          response.data &&
          response.data.data &&
          response.data.data.pdf_url
            ? response.data.data.pdf_url
            : response.data && response.data.pdf_url
            ? response.data.pdf_url
            : "";

        if (!pdfUrl) {
          setError("Invoice PDF is not available for this order.");
          return;
        }

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
      })
      .catch(() => {
        setError("Unable to print invoice.");
      });
  };

  if (loading) {
    return (
      <div className="main-content edit-order-beta">
        <ScreenLoader />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="main-content edit-order-beta">
        <div className="eob-alert is-error">{error || "Order not found."}</div>
      </div>
    );
  }

  return (
    <div className="main-content edit-order-beta">
      <div className="eob-shell">
        <header className="eob-page-head">
          <div>
            <p className="eob-kicker">Medicine Order Improved</p>
            <div className="eob-title-line">
              <h3>Edit Order #{order.id}</h3>
              <span className="eob-beta-badge">Beta</span>
            </div>
          </div>
          <div className="eob-head-actions">
            <button
              type="button"
              className="eob-icon-button"
              title="Refresh order"
              onClick={loadOrder}
              disabled={saving}
            >
              <i className="fa fa-refresh" />
            </button>
            <Link className="eob-ghost-button" to={`/admin/order-new/${order.id}`}>
              Classic editor
            </Link>
            <button
              type="button"
              className="eob-primary-button"
              onClick={() => history.push("/admin/medicine-order-improved")}
            >
              Back to list
            </button>
          </div>
        </header>

        {packageBenefits && (
          <section className="eob-package-strip">
            <div className="eob-package-title">
              {packageBenefits.logo ? (
                <img src={packageBenefits.logo} alt="" />
              ) : (
                <span className="eob-package-icon">
                  <i className="fa fa-gift" />
                </span>
              )}
              <div>
                <span>Package Benefits</span>
                <strong>{packageBenefits.packageName || "Active package"}</strong>
              </div>
            </div>
            <div className="eob-package-benefits">
              {packageBenefits.benefits.length === 0 && (
                <em>Package active for this customer</em>
              )}
              {packageBenefits.benefits.map((benefit) => (
                <em
                  className={benefit.tone === "priority" ? "is-priority" : ""}
                  key={`${benefit.label}-${benefit.value}`}
                >
                  <span>{benefit.label}</span>
                  <strong>{benefit.value}</strong>
                </em>
              ))}
            </div>
          </section>
        )}

        {(error || success || warning) && (
          <div
            className={`eob-alert ${
              error ? "is-error" : warning && !success ? "is-warning" : "is-success"
            }`}
          >
            {error || success || warning}
          </div>
        )}

        <section className="eob-summary-strip">
          <InfoRow label="Status" value={statusLabel} />
          <InfoRow label="Order Date" value={dateTime(order.order_display_date || order.order_date)} />
          <InfoRow label="Source" value={SOURCE_LABELS[order.source] || order.source} />
          <InfoRow label="Order Type" value={Number(order.order_sub_type) === 2 ? "B2B" : Number(order.order_sub_type) === 3 ? "Pickup" : Number(order.order_sub_type) === 4 ? "Purchase Request" : "B2C"} />
          <InfoRow label="Payment" value={order.paymentoption} />
        </section>

        <div className="eob-grid">
          <main className="eob-main">
            <section className="eob-panel">
              <div className="eob-panel-title">
                <h4>Order Details</h4>
                <span className={`eob-status status-${order.order_status}`}>
                  {statusLabel}
                </span>
              </div>
              <div className="eob-form-grid">
                <InfoRow label="Subscriber" value={getLeadName(order)} />
                <InfoRow label="Sales Rep" value={order.sales_user_name || order.sales_user_id} />
                <InfoRow label="Flash Reference" value={order.opharma_reference || order.mypharma_reference} />
                <InfoRow label="Delivery Type" value={DELIVERY_LABELS[order.delivery_type] || order.delivery_type} />
                <InfoRow label="Rider" value={getOrderRiderLabel(order)} />
              </div>
            </section>

            {Number(order.order_sub_type) === 2 && (
              <section className="eob-panel">
                <div className="eob-panel-title">
                  <h4>PO Intake</h4>
                  <span>{poReviewLines.length ? `${poReviewLines.length} line(s) reviewed` : "Edit workflow"}</span>
                </div>
                <div className="eob-po-edit-panel">
                  <div className="eob-po-number-row">
                    <label>
                      <span>PO Number</span>
                      <input
                        value={poNumberDraft}
                        onChange={(event) => setPoNumberDraft(event.target.value)}
                        placeholder="Enter PO number"
                      />
                    </label>
                    <button
                      type="button"
                      className="eob-primary-button"
                      disabled={saving}
                      onClick={savePoDetails}
                    >
                      {saving ? "Saving..." : "Save PO details"}
                    </button>
                  </div>
                  <div className="eob-po-docs">
                    {poUploadedUrls.length === 0 && <span>No PO document attached yet.</span>}
                    {poUploadedUrls.map((url, index) => (
                      <div className="eob-po-doc-row" key={`${url}-${index}`}>
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <i className="fa fa-file-text-o" /> PO {index + 1}
                        </a>
                        <button
                          type="button"
                          className="eob-link-button"
                          onClick={() =>
                            setPoUploadedUrls((current) =>
                              current.filter((_, urlIndex) => urlIndex !== index),
                            )
                          }
                        >
                          Remove
                        </button>
                        <button
                          type="button"
                          className="eob-ghost-button"
                          disabled={poProcessing}
                          onClick={() =>
                            processPoForReview({
                              url,
                              label: `PO ${index + 1}`,
                            })
                          }
                        >
                          {poProcessing ? "Processing..." : "Process PO"}
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="eob-po-upload-row">
                    <label>
                      <span>Upload and process a new PO</span>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv,.docx"
                        onChange={(event) => {
                          const selectedFile =
                            event.target.files && event.target.files[0]
                              ? event.target.files[0]
                              : null;
                          setPoFile(selectedFile);
                          if (selectedFile) {
                            processPoForReview({
                              file: selectedFile,
                              label: selectedFile.name,
                            });
                          }
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="eob-ghost-button"
                      disabled={!poFile || poUploading}
                      onClick={uploadPoDocument}
                    >
                      {poUploading ? "Uploading..." : "Upload PO"}
                    </button>
                    <button
                      type="button"
                      className="eob-ghost-button"
                      disabled={!poFile || poUploading || saving}
                      onClick={async () => {
                        const urls = await uploadPoDocument();
                        if (urls.length) {
                          await savePoDetails(urls);
                        }
                      }}
                    >
                      Upload & save
                    </button>
                  </div>
                  {poAppliedLines.length > 0 && (
                    <div className="eob-po-applied-note">
                      {poAppliedLines.length} PO line(s) reviewed from the latest processed PO.
                    </div>
                  )}
                </div>
              </section>
            )}

            <section className="eob-panel">
              <div className="eob-panel-title">
                <h4>Delivery Address</h4>
                <Link to={`/admin/order-new/${order.id}`}>Edit address</Link>
              </div>
              <div className="eob-address-box">
                <strong>{order.address && order.address.name}</strong>
                <span>{getAddressText(order.address)}</span>
                <span>{order.address && order.address.mobile}</span>
              </div>
            </section>

            <section className="eob-panel">
              <div className="eob-panel-title">
                <h4>Items</h4>
                <div className="eob-title-tools">
                  {itemDetailsLoading && <span>Refreshing stock...</span>}
                  <button
                    type="button"
                    className="eob-link-button"
                    onClick={() => addManualItemRows(1)}
                  >
                    Add item
                  </button>
                  {Number(order.order_sub_type) === 2 ? (
                    <div className="eob-quick-add">
                      {[2, 3, 5].map((count) => (
                        <button
                          type="button"
                          key={count}
                          onClick={() => addManualItemRows(count)}
                        >
                          +{count}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <Link to={`/admin/order-new/${order.id}`}>Manage items</Link>
                  <strong>
                    {visibleItemRows.length === items.length
                      ? `${items.length} items`
                      : `${visibleItemRows.length} of ${items.length} items`}
                  </strong>
                </div>
              </div>
              <div className="eob-order-flags">
                <label
                  className={
                    getOrderFlagValue(orderDraft, order, "refillable", "refillable_order", "is_refillable")
                      ? "is-on"
                      : ""
                  }
                >
                  <input
                    checked={getOrderFlagValue(orderDraft, order, "refillable", "refillable_order", "is_refillable")}
                    type="checkbox"
                    onChange={(event) => updateOrderDraft("refillable", event.target.checked)}
                  />
                  Refillable order
                </label>
                <label
                  className={
                    getOrderFlagValue(orderDraft, order, "pendingItem", "is_pending_item", "pending_item", "partial_sent")
                      ? "is-on"
                      : ""
                  }
                >
                  <input
                    checked={getOrderFlagValue(orderDraft, order, "pendingItem", "is_pending_item", "pending_item", "partial_sent")}
                    type="checkbox"
                    onChange={(event) => updateOrderDraft("pendingItem", event.target.checked)}
                  />
                  Pending item
                </label>
                <label
                  className={
                    getOrderFlagValue(orderDraft, order, "abusable", "abusable_drug", "is_abusable_drug", "is_abusable")
                      ? "is-on"
                      : ""
                  }
                >
                  <input
                    checked={getOrderFlagValue(orderDraft, order, "abusable", "abusable_drug", "is_abusable_drug", "is_abusable")}
                    type="checkbox"
                    onChange={(event) => updateOrderDraft("abusable", event.target.checked)}
                  />
                  Abusable drug
                </label>
                <label
                  className={
                    getOrderFlagValue(orderDraft, order, "claimOrder", "claim_order", "is_claim_order", "is_claimable")
                      ? "is-on"
                      : ""
                  }
                >
                  <input
                    checked={getOrderFlagValue(orderDraft, order, "claimOrder", "claim_order", "is_claim_order", "is_claimable")}
                    type="checkbox"
                    onChange={(event) => updateOrderDraft("claimOrder", event.target.checked)}
                  />
                  Claim order
                </label>
              </div>
              <div className="eob-item-toolbar">
                <label className="eob-item-search">
                  <i className="fa fa-search" />
                  <input
                    value={itemSearch}
                    onChange={(event) => setItemSearch(event.target.value)}
                    placeholder="Find medicine, barcode, code, placement"
                  />
                  {itemSearch ? (
                    <button
                      type="button"
                      onClick={() => setItemSearch("")}
                      aria-label="Clear item search"
                    >
                      <i className="fa fa-times" />
                    </button>
                  ) : null}
                </label>
                <div className="eob-item-filters">
                  {[
                    ["all", "All"],
                    ["unpacked", "Unpacked"],
                    ["shortage", "Low stock"],
                    ["unmatched", "Needs medicine"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={itemFilter === value ? "is-active" : ""}
                      onClick={() => setItemFilter(value)}
                    >
                      {label}
                      <span>{itemFilterCounts[value]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="eob-items">
                {items.length === 0 && <div className="eob-empty">No items found.</div>}
                {items.length > 0 && visibleItemRows.length === 0 && (
                  <div className="eob-empty">No items match this search.</div>
                )}
                {visibleItemRows.map((row, visibleIndex) => {
                  const {
                    item,
                    index,
                    itemKey,
                    itemDraft,
                    inventory,
                    itemCode,
                    barcode,
                    itemTitle,
                    unitPrice,
                    quantity,
                    isAvailable,
                    isPrintable,
                    isClaimable,
                    isUpseller,
                    isPacked,
                    subtotal,
                    belowReorder,
                  } = row;
                  return (
                    <div
                      className={`eob-item ${belowReorder ? "is-low-stock" : ""} ${isPacked ? "is-packed-item" : ""}`}
                      key={itemKey}
                    >
                      <div className="eob-item-top">
                        <div className="eob-item-main">
                          <div className="eob-item-name-line">
                            <span className="eob-item-index">{index + 1}</span>
                            <label className="eob-pack-toggle" title="Mark item packed">
                              <input
                                checked={isPacked}
                                type="checkbox"
                                onChange={(event) =>
                                  updateItemDraft(itemKey, {
                                    packed: event.target.checked,
                                  })
                                }
                              />
                            </label>
                            <strong>{itemTitle}</strong>
                          </div>
                          <div className="eob-edit-line">
                            <input
                              value={itemDraft.batchNo || ""}
                              onChange={(event) =>
                                updateItemDraft(itemKey, {
                                  batchNo: event.target.value,
                                })
                              }
                              placeholder="Batch No"
                            />
                            <div className="eob-edit-medicine">
                              <input
                                ref={(node) => {
                                  if (node) {
                                    editMedicineInputRefs.current[itemKey] = node;
                                  } else {
                                    delete editMedicineInputRefs.current[itemKey];
                                  }
                                }}
                                value={itemTitle === "-" ? "" : itemTitle}
                                onChange={(event) => {
                                  setActiveEditMedicineLineId(itemKey);
                                  updateItemDraft(itemKey, {
                                    medicationId: "",
                                    title: event.target.value,
                                  });
                                }}
                                onFocus={() => {
                                  setActiveEditMedicineLineId(itemKey);
                                  updateItemDraft(itemKey, { title: itemTitle });
                                }}
                                onKeyDown={(event) =>
                                  handleEditMedicineKeyDown(event, itemKey)
                                }
                                placeholder="Medicine"
                              />
                              {activeEditMedicineLineId === itemKey &&
                              itemTitle.trim().length >= 3 ? (
                                <div className="eob-inline-results">
                                  {itemDetailsLoading ? (
                                    <span>Searching medicines...</span>
                                  ) : null}
                                  {editMedicineResults.map((medicine, resultIndex) => (
                                    <button
                                      key={medicine.id}
                                      className={
                                        resultIndex === editMedicineHighlightIndex
                                          ? "is-active"
                                          : ""
                                      }
                                      type="button"
                                      onMouseEnter={() =>
                                        setEditMedicineHighlightIndex(resultIndex)
                                      }
                                      onMouseDown={(event) =>
                                        event.preventDefault()
                                      }
                                      onClick={() =>
                                        selectEditLineMedicine(itemKey, medicine)
                                      }
                                    >
                                      <strong>{medicine.title}</strong>
                                      <span>
                                        Rs. {money(medicine.price)} | Stock{" "}
                                        {toSafeNumber(medicine.availableQuantity, 0)}
                                      </span>
                                    </button>
                                  ))}
                                  {!editMedicineResults.length &&
                                  !itemDetailsLoading ? (
                                    <span>No matches yet.</span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="eob-item-meta">
                            <span>Item code: {itemCode}</span>
                            <span>Barcode: {barcode}</span>
                            <span>Placement: {inventory.placement}</span>
                          </div>
                          {getBatchRows(item).length > 0 && (
                            <div className="eob-batches">
                              {getBatchRows(item).map((batch, batchIndex) => (
                                <em key={`${item.id || item.medication_id}-${batchIndex}`}>
                                  {getBatchLabel(batch) || `Batch ${batchIndex + 1}`}
                                </em>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="eob-item-numbers">
                          <button
                            className="eob-item-delete"
                            type="button"
                            title="Remove item"
                            onClick={() => removeOrderItemRow(item, index)}
                          >
                            <i className="fa fa-trash" />
                          </button>
                          <label>
                            <span>Qty</span>
                            <input
                              min="1"
                              type="number"
                              ref={(node) => {
                                if (node) {
                                  editQuantityInputRefs.current[itemKey] = node;
                                } else {
                                  delete editQuantityInputRefs.current[itemKey];
                                }
                              }}
                              value={quantity}
                              onChange={(event) =>
                                updateItemDraft(itemKey, {
                                  quantity: event.target.value,
                                })
                              }
                              onKeyDown={(event) =>
                                handleEditQuantityKeyDown(
                                  event,
                                  itemKey,
                                  visibleIndex,
                                )
                              }
                            />
                          </label>
                          <label>
                            <span>Unit</span>
                            <input
                              min="0"
                              step="0.01"
                              type="number"
                              value={unitPrice}
                              onChange={(event) =>
                                updateItemDraft(itemKey, {
                                  unitPrice: event.target.value,
                                })
                              }
                            />
                          </label>
                          <strong>{money(subtotal)}</strong>
                        </div>
                      </div>
                      <div className="eob-item-checks">
                        <label className={isPacked ? "is-packed" : ""}>
                          <input
                            checked={isPacked}
                            type="checkbox"
                            onChange={(event) =>
                              updateItemDraft(itemKey, {
                                packed: event.target.checked,
                              })
                            }
                          />
                          Packed
                        </label>
                        <label className={isAvailable ? "is-on" : ""}>
                          <input
                            checked={isAvailable}
                            type="checkbox"
                            onChange={(event) =>
                              updateItemDraft(itemKey, {
                                available: event.target.checked,
                              })
                            }
                          />
                          Available
                        </label>
                        <label className={isPrintable ? "is-on" : ""}>
                          <input
                            checked={isPrintable}
                            type="checkbox"
                            onChange={(event) =>
                              updateItemDraft(itemKey, {
                                printable: event.target.checked,
                              })
                            }
                          />
                          Printable
                        </label>
                        <label className={isClaimable ? "is-on" : ""}>
                          <input
                            checked={isClaimable}
                            type="checkbox"
                            onChange={(event) =>
                              updateItemDraft(itemKey, {
                                claimable: event.target.checked,
                              })
                            }
                          />
                          Claimable
                        </label>
                        <label className={isUpseller ? "is-on" : ""}>
                          <input
                            checked={isUpseller}
                            type="checkbox"
                            onChange={(event) =>
                              updateItemDraft(itemKey, {
                                upseller: event.target.checked,
                              })
                            }
                          />
                          Upseller
                        </label>
                        <span className={getBooleanValue(item.refill_requested, item.refillRequested) ? "is-on" : ""}>
                          Refill requested
                        </span>
                      </div>
                      <div className="eob-stock-board">
                        <div className="eob-stock-label">Stock Availability</div>
                        <div className="eob-stock-metric">
                          <span>Warehouse inventory</span>
                          <strong className={inventory.warehouseStock > 0 ? "is-good" : "is-alert"}>
                            {inventory.warehouseStock}
                          </strong>
                        </div>
                        <div className="eob-stock-metric">
                          <span>Pharmacy inventory</span>
                          <strong className={inventory.pharmacyStock > 0 ? "is-good" : "is-alert"}>
                            {inventory.pharmacyStock}
                          </strong>
                        </div>
                        <div className="eob-stock-metric">
                          <span>Medicine inventory</span>
                          <strong className={inventory.totalStock > 0 ? "is-good" : "is-alert"}>
                            {inventory.totalStock}
                          </strong>
                        </div>
                        <div className="eob-stock-metric">
                          <span>Consumption level</span>
                          <strong>{inventory.consumptionLevel}</strong>
                        </div>
                        <div className="eob-stock-metric">
                          <span>Reorder level</span>
                          <strong className={belowReorder ? "is-alert" : "is-good"}>
                            {inventory.reorderLevel}
                          </strong>
                        </div>
                        <Link className="eob-mini-action" to={`/admin/order-new/${order.id}`}>
                          Request Refill
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="eob-item-save-bar">
                <div className="eob-bottom-quick-add">
                  <button
                    type="button"
                    className="eob-ghost-button"
                    onClick={() => addManualItemRows(1)}
                  >
                    <i className="fa fa-plus" /> Add item
                  </button>
                  {Number(order.order_sub_type) === 2 ? (
                    <>
                      <button type="button" className="eob-ghost-button" onClick={() => addManualItemRows(2)}>
                        Add 2 rows
                      </button>
                      <button type="button" className="eob-ghost-button" onClick={() => addManualItemRows(3)}>
                        Add 3 rows
                      </button>
                      <button type="button" className="eob-ghost-button" onClick={() => addManualItemRows(5)}>
                        Add 5 rows
                      </button>
                    </>
                  ) : null}
                </div>
                <button
                  className="eob-primary-button"
                  type="button"
                  disabled={saving}
                  onClick={saveOrderItemChanges}
                >
                  {saving ? "Saving..." : "Save item changes"}
                </button>
              </div>
            </section>
          </main>

          <aside className="eob-side">
            <section className="eob-panel">
              <h4>Customer Info</h4>
              <InfoRow label="Name" value={getLeadName(order)} />
              <InfoRow label="Mobile Number" value={getMobile(order)} />
              <InfoRow label="Email Address" value={getEmail(order)} />
              <InfoRow label="Remaining Credits" value={money(order.remainingCredit || order.remaining_credit)} />
              <InfoRow label="Company Name" value={getCompany(order)} />
            </section>

            <section className="eob-panel">
              <h4>Basket Info</h4>
              <InfoRow label="Basket Value" value={money(order.basket_value)} />
              <InfoRow label="Flash Credit" value={money(order.used_credit_points)} />
              <InfoRow label="Discounted Amount" value={money(order.organization_discount_cost)} />
              <InfoRow label="Delivery Charge" value={money(order.delivery_charge)} />
              {addonBenefitRows(order).map((benefit) => (
                <InfoRow
                  key={`addon-benefit-${benefit.id}`}
                  label={`Applied Add-on Benefit: ${benefit.label || benefit.addon_name || "Applied benefit"}`}
                  value={addonBenefitValue(benefit)}
                />
              ))}
              {addonBenefitPreviewRows(order).map((benefit) => (
                <InfoRow
                  key={`addon-benefit-preview-${benefit.id}`}
                  label={`Eligible Add-on Benefit: ${benefit.label || benefit.addon_name || "Eligible benefit"}`}
                  value={addonBenefitValue(benefit)}
                />
              ))}
              <InfoRow label="Total Cost" value={money(totalCost)} />
              <InfoRow label="Pending Amount" value={money(pendingAmount)} />
            </section>

            <section className="eob-panel">
              <h4>Documents</h4>
              <div className="eob-doc-list">
                {documents.length === 0 && <span>No documents attached.</span>}
                {documents.map((document) => (
                  <a
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={`${document.label}-${document.url}`}
                  >
                    <i className="fa fa-file-text-o" />
                    {document.label}
                  </a>
                ))}
              </div>
            </section>
          </aside>
        </div>

        <section className="eob-panel eob-comments-panel">
          {commentError && <div className="eob-alert is-error">{commentError}</div>}
          <CommentSection
            title="Order Comments"
            comments={comments}
            onAddComment={handleAddComment}
            onDeleteComment={handleDeleteComment}
            userOptions={userListData}
            UserListData={userListData}
            onMentionChange={setMentionedUsers}
          />
        </section>
      </div>

      <footer className="eob-footer">
        <div className="eob-footer-primary">
          <button type="button" className="eob-ghost-button" disabled={saving} onClick={saveFullUpdate}>
            {saving ? "Saving..." : "Full update"}
          </button>
          <button type="button" className="eob-ghost-button" onClick={printInvoice}>
            Print Invoice <i className="fas fa-file-invoice" />
          </button>
        </div>
        <div className="eob-footer-status">
          {statusActions.map((status) => (
            <button
              type="button"
              className={`eob-status-action status-${status}`}
              key={status}
              disabled={saving}
              onClick={() => {
                if (status === 5) {
                  setCancelOpen(true);
                  return;
                }
                if (status === 11 || status === 3) {
                  openRiderStatusModal(status);
                  return;
                }
                updateStatus(status);
              }}
            >
              {ORDER_STATUSES[status]}
            </button>
          ))}
        </div>
      </footer>

      {poReviewOpen && (
        <div className="eob-modal-backdrop">
          <div className="eob-modal eob-po-review-modal">
            <div className="eob-po-review-head">
              <div>
                <h4>Review PO Lines</h4>
                <p>{poReviewSource || "Attached PO"} | Review all PO rows, then match medicines before updating this order.</p>
              </div>
              <div className="eob-po-modal-summary">
                <span>{poSelectedCount} selected</span>
                <span>{poShortageCount} shortage</span>
                <span>{poRefillCount} refill</span>
              </div>
              <button
                type="button"
                className="eob-danger-button"
                onClick={() => setPoReviewOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="eob-po-review-table-wrap">
              <div className="eob-po-modal-toolbar">
                <button
                  className="eob-ghost-button"
                  type="button"
                  onClick={addPoReviewLine}
                >
                  <i className="fa fa-plus" /> Add item
                </button>
                <button
                  className="eob-ghost-button"
                  disabled={poProcessing}
                  type="button"
                  onClick={recheckPoReviewLines}
                >
                  <i className="fa fa-refresh" /> {poProcessing ? "Rechecking..." : "Recheck matches"}
                </button>
                <span>Matched lines will fill the order. Shortages can create one refill request.</span>
              </div>
              <table className="eob-po-review-table">
                <thead>
                  <tr>
                    <th>Use</th>
                    <th>PO Requested Item</th>
                    <th>Flash Medicine</th>
                    <th>Qty</th>
                    <th>Stock</th>
                    <th>Refill</th>
                    <th>PO price</th>
                    <th>System price</th>
                  </tr>
                </thead>
                <tbody>
                  {poReviewLines.map((line, index) => {
                    const hasShortage = line.medication && getShortageQuantity(line) > 0;
                    return (
                    <tr
                      key={`${line.requestedName}-${index}`}
                      className={[
                        !line.medication ? "is-unmatched" : "",
                        line.selected ? "is-selected" : "is-muted",
                        hasShortage ? "is-low-stock" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={line.selected}
                          onChange={(event) =>
                            updatePoReviewLine(index, "selected", event.target.checked)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={line.requestedName}
                          onChange={(event) =>
                            updatePoReviewLine(index, "requestedName", event.target.value)
                          }
                          placeholder="Requested item"
                        />
                        {line.requestedUnit ? <span>{line.requestedUnit}</span> : null}
                      </td>
                      <td>
                        {line.medication ? (
                          <>
                            <strong>{getMedicationTitle(line.medication)}</strong>
                            <span>
                              Rs. {money(getMedicationPrice(line.medication))}
                              {getMedicationItemCode(line.medication)
                                ? ` | Code ${getMedicationItemCode(line.medication)}`
                                : ""}
                            </span>
                            <button
                              type="button"
                              onClick={() => openPoMedicineSearch(index)}
                            >
                              Change medicine
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openPoMedicineSearch(index)}
                          >
                            Search medicine
                          </button>
                        )}
                        {poMedicineSearchIndex === index && (
                          <div className="eob-po-search-box">
                            <input
                              value={poMedicineSearchQuery}
                              onChange={(event) => setPoMedicineSearchQuery(event.target.value)}
                              placeholder="Search medicine name or item code"
                            />
                            <div>
                              {poMedicineSearchResults.map((medicine) => (
                                <button
                                  key={medicine.id}
                                  type="button"
                                  onClick={() => selectPoMedication(index, medicine)}
                                >
                                  <strong>{getMedicationTitle(medicine)}</strong>
                                  <span>
                                    Rs. {money(getMedicationPrice(medicine))}
                                    {getMedicationItemCode(medicine)
                                      ? ` | Code ${getMedicationItemCode(medicine)}`
                                      : ""}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          value={line.quantity}
                          onChange={(event) =>
                            updatePoReviewLine(index, "quantity", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        {line.medication ? (
                          <>
                            <strong>{toSafeNumber(line.availableQuantity, 0)}</strong>
                            {getShortageQuantity(line) > 0 ? (
                              <span className="eob-stock-short">
                                Short {getShortageQuantity(line)}
                              </span>
                            ) : (
                              <span className="eob-stock-ok">Enough stock</span>
                            )}
                          </>
                        ) : (
                          <span>Select medicine</span>
                        )}
                      </td>
                      <td>
                        {!line.medication ? (
                          <span>Match first</span>
                        ) : hasShortage ? (
                          <div className="eob-refill-cell">
                            <label>
                              <input
                                checked={line.refillRequested}
                                type="checkbox"
                                onChange={(event) =>
                                  updatePoReviewLine(index, "refillRequested", event.target.checked)
                                }
                              />
                              Request
                            </label>
                            <input
                              min="1"
                              placeholder="Qty"
                              type="number"
                              value={line.refillQuantity}
                              onChange={(event) =>
                                updatePoReviewLine(index, "refillQuantity", event.target.value)
                              }
                            />
                            <input
                              value={line.refillRemark}
                              onChange={(event) =>
                                updatePoReviewLine(index, "refillRemark", event.target.value)
                              }
                              placeholder="Remark"
                            />
                          </div>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Not fetched"
                          value={line.requestedPrice}
                          onChange={(event) =>
                            updatePoReviewLine(index, "requestedPrice", event.target.value)
                          }
                        />
                        {line.requestedTotal ? (
                          <span>Total Rs. {money(line.requestedTotal)}</span>
                        ) : null}
                        {getPoUnitPrice(line) === "" ? (
                          <span className="eob-po-missing">PO price missing</span>
                        ) : null}
                      </td>
                      <td>
                        {line.medication ? (
                          <>
                            <strong>Rs. {money(getMedicationPrice(line.medication))}</strong>
                            {line.requestedPrice ? (
                              <span>
                                Diff Rs.{" "}
                                {money(
                                  toSafeNumber(line.requestedPrice, 0) -
                                    toSafeNumber(getMedicationPrice(line.medication), 0),
                                )}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
            <div className="eob-po-review-footer">
              <div className="eob-po-total-strip">
                <span>
                  <b>PO total</b>
                  Rs. {money(poReviewPoTotal)}
                </span>
                <span>
                  <b>Medication total</b>
                  Rs. {money(poReviewMedicationTotal)}
                </span>
                <span
                  className={
                    Math.abs(poReviewTotalDifference) > 0.01 || poMissingPriceCount
                      ? "eob-po-total-diff eob-po-total-diff-alert"
                      : "eob-po-total-diff"
                  }
                >
                  <b>Difference</b>
                  Rs. {money(poReviewTotalDifference)}
                </span>
                {poMissingPriceCount ? (
                  <em>{poMissingPriceCount} selected line(s) missing PO price</em>
                ) : null}
              </div>
              <button
                type="button"
                className="eob-ghost-button"
                onClick={() => setPoReviewOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eob-primary-button"
                disabled={poReviewApplying}
                onClick={applyPoReviewLines}
              >
                <i className="fa fa-check" />{" "}
                {poReviewApplying ? "Applying lines..." : "Apply selected lines"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelOpen && (
        <div className="eob-modal-backdrop">
          <div className="eob-modal">
            <h4>Cancel Order</h4>
            <label>
              <span>Cancel Reason</span>
              <input
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
              />
            </label>
            <label>
              <span>Cancel Message</span>
              <textarea
                rows="3"
                value={cancelMessage}
                onChange={(event) => setCancelMessage(event.target.value)}
              />
            </label>
            <div className="eob-modal-actions">
              <button
                type="button"
                className="eob-ghost-button"
                onClick={() => setCancelOpen(false)}
              >
                Keep Order
              </button>
              <button
                type="button"
                className="eob-danger-button"
                disabled={saving || !cancelReason.trim()}
                onClick={() => updateStatus(5, cancelReason, cancelMessage)}
              >
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {riderModalOpen && (
        <div className="eob-modal-backdrop">
          <div className="eob-modal">
            <h4>
              Select Rider for {ORDER_STATUSES[pendingRiderStatus] || "Status Change"}
            </h4>
            <label>
              <span>Rider</span>
              <select
                value={selectedRiderId}
                onChange={(event) => {
                  setSelectedRiderId(event.target.value);
                  setRiderError("");
                }}
                disabled={riderLoading || saving}
              >
                <option value="">
                  {riderLoading ? "Loading riders..." : "Select Rider"}
                </option>
                {riderOptions.map((rider) => (
                  <option key={rider.value} value={rider.value}>
                    {rider.label}
                  </option>
                ))}
              </select>
            </label>
            {riderError ? <div className="eob-field-error">{riderError}</div> : null}
            <div className="eob-modal-actions">
              <button
                type="button"
                className="eob-ghost-button"
                onClick={closeRiderStatusModal}
                disabled={saving}
              >
                Close
              </button>
              <button
                type="button"
                className="eob-primary-button"
                disabled={saving || riderLoading}
                onClick={updateStatusWithRider}
              >
                {saving ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditOrderBeta;
